import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Typography,
  Button,
} from '@mui/material';
import {
  LightMode,
  DarkMode,
  SettingsBrightness,
  Check,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import type { ThemeMode } from '../contexts/ThemeContext';

interface ThemeOption {
  mode: ThemeMode;
  label: string;
  icon: React.ReactElement;
  description: string;
}

const themeOptions: ThemeOption[] = [
  {
    mode: 'light',
    label: 'Light',
    icon: <LightMode />,
    description: 'Light theme',
  },
  {
    mode: 'dark',
    label: 'Dark',
    icon: <DarkMode />,
    description: 'Dark theme',
  },
];

interface ThemeToggleProps {
  variant?: 'icon' | 'menu';
  showLabel?: boolean;
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = 'icon',
  showLabel = false,
}) => {
  const { mode, isDark, setMode } = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (variant === 'icon') {
      setMode(isDark ? 'light' : 'dark');
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleModeSelect = (selectedMode: ThemeMode) => {
    setMode(selectedMode);
    handleClose();
  };

  const getCurrentIcon = () => {
    const option = themeOptions.find((opt) => opt.mode === mode);
    return option ? option.icon : <SettingsBrightness />;
  };

  if (variant === 'icon') {
    return (
      <Tooltip title={`Switch to ${isDark ? 'light' : 'dark'} theme`}>
        <IconButton onClick={handleClick} color="inherit" size="medium">
          {getCurrentIcon()}
        </IconButton>
      </Tooltip>
    );
  }

  if (showLabel) {
    return (
      <Box
        sx={{
          display: 'flex',
          p: 0.5,
          bgcolor: 'background.default',
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {themeOptions.map((option) => (
          <Tooltip title={option.description} key={option.mode} placement="top">
            <Button
              onClick={() => setMode(option.mode)}
              fullWidth
              sx={{
                py: 1,
                px: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                borderRadius: '8px',
                backgroundColor:
                  mode === option.mode ? 'action.selected' : 'transparent',
                color:
                  mode === option.mode ? 'text.primary' : 'text.secondary',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor:
                    mode === option.mode
                      ? 'action.selected'
                      : 'action.hover',
                  color: 'text.primary',
                },
                minWidth: 'auto',
              }}
            >
              {React.cloneElement(option.icon, { sx: { fontSize: 20 } })}
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'none',
                  lineHeight: 1.2,
                  fontSize: '0.65rem',
                }}
              >
                {option.label}
              </Typography>
            </Button>
          </Tooltip>
        ))}
      </Box>
    );
  }

  return (
    <>
      <Tooltip title="Change theme">
        <IconButton onClick={handleClick} color="inherit" size="medium">
          {getCurrentIcon()}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: 2,
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                : '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        {themeOptions.map((option) => (
          <MenuItem
            key={option.mode}
            onClick={() => handleModeSelect(option.mode)}
            selected={mode === option.mode}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
            {mode === option.mode && <Check sx={{ ml: 'auto' }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ThemeToggle;
